import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {
  ApiResponse,
  AuthSession,
  AuthUserPayload,
  LoginSuccessPayload,
  MePayload,
  RefreshTokenPayload
} from '../../models/auth/auth-session.model';
import { CookiesService } from '../cookies/cookies.service';

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  locale?: string;
  timezone?: string;
}

export interface RegisterConfirmRequest {
  email: string;
  code: string;
  company: {
    displayName: string;
    legalName: string;
    industry: string;
    country: string;
    city: string;
    website?: string;
    subdomain: string;
    size: number;
    billingEmail: string;
    about?: string;
  };
  security: {
    captchaToken: string;
  };
  consents: {
    tosAccepted: boolean;
    privacyAccepted: boolean;
  };
  metadata?: {
    signupSource?: string;
    referralCode?: string | null;
  };
}

export type PendingRegisterConfirmRequest = Omit<RegisterConfirmRequest, 'code'>;

interface PasswordSuccessPayload {
  status: string;
}

export interface AuthError extends Error {
  code?: string;
  status?: number;
  details?: unknown;
}

interface PasswordChallenge {
  email: string;
  session: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly storageKey = 'signly.auth.session';
  private readonly pendingRegisterRequestKey = 'signly.auth.pendingRegisterRequest';
  private readonly pendingRegisterConfirmRequestKey = 'signly.auth.pendingRegisterConfirmRequest';
  private session: AuthSession | null;
  private recoveryEmail: string | null = null;
  private recoveryOtp: string | null = null;
  private pendingRecoveryPassword: string | null = null;
  private passwordChallenge: PasswordChallenge | null = null;
  private pendingRegisterRequest: RegisterRequest | null = null;
  private pendingRegisterConfirmRequest: PendingRegisterConfirmRequest | null = null;

  constructor(private http: HttpClient, private cookiesService: CookiesService) {
    this.session = this.loadSession();
  }

  login(email: string, password: string): Observable<AuthSession> {
    const url = `${this.baseUrl}/auth/login`;
    return this.http
      .post<ApiResponse<LoginSuccessPayload>>(url, { email, password })
      .pipe(
        map(res => this.mapSession(res.data)),
        tap(session => this.persistSession(session)),
        switchMap(session =>
          this.me().pipe(
            map(() => this.session ?? session),
            catchError(() => of(this.session ?? session))
          )
        ),
        catchError(err => this.handleError(err))
      );
  }

  register(payload: RegisterRequest): Observable<ApiResponse<unknown>> {
    const url = `${this.baseUrl}/auth/register`;
    return this.http
      .post<ApiResponse<unknown>>(url, payload)
      .pipe(catchError(err => this.handleError(err)));
  }

  confirmRegister(payload: RegisterConfirmRequest): Observable<ApiResponse<unknown>> {
    const url = `${this.baseUrl}/auth/register/confirm`;
    return this.http
      .post<ApiResponse<unknown>>(url, payload)
      .pipe(catchError(err => this.handleError(err)));
  }

  resendRegisterOtp(email: string): Observable<ApiResponse<unknown>> {
    const url = `${this.baseUrl}/auth/register/otp/resend`;
    return this.http
      .post<ApiResponse<unknown>>(url, { email })
      .pipe(catchError(err => this.handleError(err)));
  }

  setPendingRegistration(
    registerRequest: RegisterRequest,
    confirmRequest: PendingRegisterConfirmRequest
  ): void {
    this.pendingRegisterRequest = {
      ...registerRequest
    };
    this.pendingRegisterConfirmRequest = {
      ...confirmRequest,
      company: {
        ...confirmRequest.company
      },
      security: {
        ...confirmRequest.security
      },
      consents: {
        ...confirmRequest.consents
      },
      metadata: confirmRequest.metadata ? { ...confirmRequest.metadata } : undefined
    };
    this.savePendingRegistrationToStorage();
  }

  getPendingRegisterRequest(): RegisterRequest | null {
    if (!this.pendingRegisterRequest) {
      this.loadPendingRegistrationFromStorage();
    }
    return this.pendingRegisterRequest ? { ...this.pendingRegisterRequest } : null;
  }

  getPendingRegisterConfirmRequest(): PendingRegisterConfirmRequest | null {
    if (!this.pendingRegisterConfirmRequest) {
      this.loadPendingRegistrationFromStorage();
    }
    if (!this.pendingRegisterConfirmRequest) {
      return null;
    }

    return {
      ...this.pendingRegisterConfirmRequest,
      company: {
        ...this.pendingRegisterConfirmRequest.company
      },
      security: {
        ...this.pendingRegisterConfirmRequest.security
      },
      consents: {
        ...this.pendingRegisterConfirmRequest.consents
      },
      metadata: this.pendingRegisterConfirmRequest.metadata
        ? { ...this.pendingRegisterConfirmRequest.metadata }
        : undefined
    };
  }

  clearPendingRegistration(): void {
    this.pendingRegisterRequest = null;
    this.pendingRegisterConfirmRequest = null;
    this.clearPendingRegistrationFromStorage();
  }

  logout(): Observable<void> {
    const url = `${this.baseUrl}/auth/logout`;
    return this.http
      .post<ApiResponse<{ status: string }>>(url, {}, { headers: this.buildSignlyHeaders() })
      .pipe(
        map(() => void 0),
        tap(() => this.persistSession(null)),
        catchError(err => {
          this.persistSession(null);
          return this.handleError(err);
        })
      );
  }

  refreshToken(refreshToken?: string): Observable<AuthSession> {
    const token = refreshToken || this.session?.refreshToken;
    if (!token) {
      return throwError(() => new Error('No hay token de actualización disponible'));
    }

    const url = `${this.baseUrl}/auth/token/refresh`;
    const headers = this.buildSignlyHeaders();
    return this.http
      .post<ApiResponse<RefreshTokenPayload>>(url, { refresh_token: token }, { headers })
      .pipe(
        map(res => this.mergeRefreshTokens(res.data)),
        tap(session => this.persistSession(session)),
        catchError(err => this.handleError(err))
      );
  }

  forgotPassword(email: string): Observable<ApiResponse<unknown>> {
    const url = `${this.baseUrl}/auth/password/forgot`;
    return this.http
      .post<ApiResponse<unknown>>(url, { email })
      .pipe(
        tap(() => {
          this.recoveryEmail = email;
          this.recoveryOtp = null;
        }),
        catchError(err => this.handleError(err))
      );
  }

  confirmPassword(email: string, otpCode: string, newPassword: string): Observable<ApiResponse<PasswordSuccessPayload>> {
    const url = `${this.baseUrl}/auth/password/confirm`;
    return this.http
      .post<ApiResponse<PasswordSuccessPayload>>(url, {
        email,
        otp_code: otpCode,
        new_password: newPassword
      })
      .pipe(catchError(err => this.handleError(err)));
  }

  completePassword(email: string, session: string, newPassword: string): Observable<AuthSession> {
    const url = `${this.baseUrl}/auth/password/complete`;
    return this.http
      .post<ApiResponse<LoginSuccessPayload>>(url, {
        email,
        session,
        new_password: newPassword
      })
      .pipe(
        map(res => this.mapSession(res.data)),
        tap(authSession => this.persistSession(authSession)),
        catchError(err => this.handleError(err))
      );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<ApiResponse<PasswordSuccessPayload>> {
    const url = `${this.baseUrl}/auth/password/change`;
    return this.http
      .post<ApiResponse<PasswordSuccessPayload>>(
        url,
        { old_password: oldPassword, new_password: newPassword },
        { headers: this.buildAuthorizationHeaders() }
      )
      .pipe(catchError(err => this.handleError(err)));
  }

  me(): Observable<MePayload> {
    const url = `${this.baseUrl}/auth/me`;
    const headers = this.buildSignlyHeaders();
    return this.http
      .get<ApiResponse<MePayload>>(url, { headers })
      .pipe(
        map(res => res.data),
        tap(data => this.applyMeToSession(data)),
        catchError(err => this.handleError(err))
      );
  }

  verifyOtp(email: string, otpCode: string, newPassword: string): Observable<ApiResponse<PasswordSuccessPayload>> {
    return this.confirmPassword(email, otpCode, newPassword);
  }

  setRecoveryEmail(email: string): void {
    this.recoveryEmail = email;
  }

  getRecoveryEmail(): string | null {
    return this.recoveryEmail;
  }

  clearRecoveryEmail(): void {
    this.recoveryEmail = null;
    this.recoveryOtp = null;
    this.pendingRecoveryPassword = null;
  }

  setRecoveryOtp(otp: string): void {
    this.recoveryOtp = otp;
  }

  getRecoveryOtp(): string | null {
    return this.recoveryOtp;
  }

  clearRecoveryOtp(): void {
    this.recoveryOtp = null;
  }

  setPendingRecoveryPassword(password: string): void {
    this.pendingRecoveryPassword = password;
  }

  getPendingRecoveryPassword(): string | null {
    return this.pendingRecoveryPassword;
  }

  clearPendingRecoveryPassword(): void {
    this.pendingRecoveryPassword = null;
  }

  setPasswordChallenge(challenge: PasswordChallenge): void {
    this.passwordChallenge = challenge;
  }

  getPasswordChallenge(): PasswordChallenge | null {
    return this.passwordChallenge;
  }

  clearPasswordChallenge(): void {
    this.passwordChallenge = null;
  }

  getSession(): AuthSession | null {
    return this.session;
  }

  getAccessToken(): string | null {
    return this.session?.accessToken || null;
  }

  getRefreshToken(): string | null {
    return this.session?.refreshToken || null;
  }

  isAuthenticated(): boolean {
    return !!this.session?.accessToken || !!this.session?.refreshToken;
  }

  private mapSession(payload: LoginSuccessPayload): AuthSession {
    const userPayload = payload.user;
    return {
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token,
      idToken: payload.id_token,
      tokenType: payload.token_type,
      expiresIn: payload.expires_in,
      user: this.mapUser(userPayload)
    };
  }

  private mergeRefreshTokens(payload: RefreshTokenPayload): AuthSession {
    if (!this.session) {
      throw new Error('No hay sesión activa para actualizar');
    }

    return {
      ...this.session,
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token,
      idToken: payload.id_token,
      tokenType: payload.token_type,
      expiresIn: payload.expires_in
    };
  }

  private mapUser(user: AuthUserPayload) {
    return {
      email: user.email,
      name: user.name,
      picture: user.picture,
      tenantId: user.tenant_id,
      userId: user.user_id
    };
  }

  private buildSignlyHeaders(): HttpHeaders {
    const token = this.session?.accessToken;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('X-Auth-Signly', `Bearer ${token}`);
    }
    return headers;
  }

  private buildAuthorizationHeaders(): HttpHeaders {
    const token = this.session?.accessToken;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private loadSession(): AuthSession | null {
    const fromCookie = this.cookiesService.get(this.storageKey);
    if (fromCookie) {
      try {
        return JSON.parse(fromCookie) as AuthSession;
      } catch {
        this.cookiesService.delete(this.storageKey);
      }
    }

    if (typeof localStorage !== 'undefined') {
      try {
        const legacy = localStorage.getItem(this.storageKey);
        if (legacy) {
          const session = JSON.parse(legacy) as AuthSession;
          this.cookiesService.set(this.storageKey, legacy);
          localStorage.removeItem(this.storageKey);
          return session;
        }
      } catch {
        try { localStorage.removeItem(this.storageKey); } catch { /* ignore */ }
      }
    }

    return null;
  }

  private persistSession(session: AuthSession | null): void {
    this.session = session;
    if (!session) {
      this.cookiesService.delete(this.storageKey);
      if (typeof localStorage !== 'undefined') {
        try { localStorage.removeItem(this.storageKey); } catch { /* ignore */ }
      }
      return;
    }
    this.cookiesService.set(this.storageKey, JSON.stringify(session));
    if (typeof localStorage !== 'undefined') {
      try { localStorage.removeItem(this.storageKey); } catch { /* ignore */ }
    }
    this.clearRecoveryEmail();
    this.clearRecoveryOtp();
    this.clearPasswordChallenge();
    this.clearPendingRegistration();
  }

  private applyMeToSession(payload: MePayload): void {
    if (!this.session) {
      return;
    }
    const email = payload.attributes?.email ?? this.session.user.email;
    const sub = payload.attributes?.sub ?? this.session.user.userId;
    const isRoot = this.extractIsRoot(payload);
    const merged: AuthSession = {
      ...this.session,
      user: {
        email,
        name: this.session.user.name,
        picture: this.session.user.picture,
        tenantId: payload.tenant_id,
        userId: sub,
        isRoot
      }
    };
    this.persistSession(merged);
  }

  private extractIsRoot(payload: MePayload): boolean {
    if (typeof payload.is_root === 'boolean') {
      return payload.is_root;
    }
    const attrs = payload.attributes || {};
    const candidates = [
      attrs['is_root'],
      attrs['custom:is_root'],
      attrs['root']
    ];
    for (const value of candidates) {
      if (value === undefined) continue;
      const normalized = String(value).toLowerCase().trim();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
        return true;
      }
    }
    return false;
  }

  private handleError(error: unknown) {
    const httpError = error as HttpErrorResponse;
    const backend = (httpError?.error ?? {}) as {
      code?: string;
      message?: string;
      error?: { details?: unknown };
      details?: unknown;
      status?: number;
    };
    const details =
      backend?.error?.details ??
      backend?.details ??
      null;
    const message =
      backend?.message ||
      (details as { message?: string } | null)?.message ||
      httpError?.message ||
      'Error de autenticación';
    const code =
      backend?.code ||
      (details as { reason?: string } | null)?.reason ||
      undefined;

    const authError = new Error(message) as AuthError;
    authError.code = code;
    authError.status = httpError?.status;
    authError.details = details;

    return throwError(() => authError);
  }

  private savePendingRegistrationToStorage(): void {
    try {
      if (this.pendingRegisterRequest) {
        this.cookiesService.set(this.pendingRegisterRequestKey, JSON.stringify(this.pendingRegisterRequest));
      }
      if (this.pendingRegisterConfirmRequest) {
        this.cookiesService.set(
          this.pendingRegisterConfirmRequestKey,
          JSON.stringify(this.pendingRegisterConfirmRequest)
        );
      }
    } catch {
      // ignore storage failures
    }
  }

  private loadPendingRegistrationFromStorage(): void {
    try {
      if (!this.pendingRegisterRequest) {
        const rawPendingRegister = this.cookiesService.get(this.pendingRegisterRequestKey);
        if (rawPendingRegister) {
          this.pendingRegisterRequest = JSON.parse(rawPendingRegister) as RegisterRequest;
        }
      }

      if (!this.pendingRegisterConfirmRequest) {
        const rawPendingConfirm = this.cookiesService.get(this.pendingRegisterConfirmRequestKey);
        if (rawPendingConfirm) {
          this.pendingRegisterConfirmRequest = JSON.parse(rawPendingConfirm) as PendingRegisterConfirmRequest;
        }
      }
    } catch {
      // ignore malformed storage
    }
  }

  private clearPendingRegistrationFromStorage(): void {
    try {
      this.cookiesService.delete(this.pendingRegisterRequestKey);
      this.cookiesService.delete(this.pendingRegisterConfirmRequestKey);
    } catch {
      // ignore storage failures
    }
  }
}
